import React, { useEffect, useState } from 'react';
import { TrendingUp, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';

/**
 * TagsEmAltaWidget — chips das especializações mais cadastradas na cidade.
 *
 * Cada chip é clicável e navega para /:slug/explorar?tags=<id>. Fica
 * escondido quando não há dados suficientes (menos de `minItems`).
 */
export default function TagsEmAltaWidget({
  municipioSlug,
  municipioId,
  municipioNome,
  limit = 8,
  minItems = 3,
}) {
  const [items, setItems] = useState(null); // null=loading, []=vazio
  const navigate = useNavigate();

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const params = { limit };
        if (municipioId) params.municipio_id = municipioId;
        else if (municipioSlug) params.municipio_slug = municipioSlug;
        const r = await api.get('/tags/populares', { params });
        if (!alive) return;
        setItems(r?.data?.items || []);
      } catch (_e) {
        if (alive) setItems([]);
      }
    })();
    return () => { alive = false; };
  }, [municipioId, municipioSlug, limit]);

  // Loading skeleton (não renderiza título até saber que tem dados)
  if (items === null) {
    return null;
  }
  if (items.length < minItems) {
    return null;
  }

  const handleClick = (tag) => {
    // Preferimos a primeira categoria da tag como âncora de navegação
    // (assim o filtro de tags aparece já habilitado no Explorar).
    const cat = (tag.categoria_ids || [])[0];
    const slug = municipioSlug;
    if (!slug) return;
    if (cat) {
      navigate(`/${slug}/${cat}?tags=${encodeURIComponent(tag.id)}`);
    } else {
      navigate(`/${slug}/explorar?tags=${encodeURIComponent(tag.id)}`);
    }
  };

  return (
    <section
      className="bg-gradient-to-br from-[var(--primary)]/5 to-white border border-[var(--primary)]/10 rounded-2xl p-6"
      data-testid="tags-em-alta-widget"
    >
      <div className="flex items-center gap-2 mb-1">
        <TrendingUp className="w-5 h-5 text-[var(--primary)]" />
        <h3 className="text-lg font-bold text-gray-900" style={{ fontFamily: 'Manrope' }}>
          Em alta{municipioNome ? ` em ${municipioNome}` : ''}
        </h3>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        As especializações mais buscadas por aqui — clique para explorar
      </p>
      <div className="flex flex-wrap gap-2" data-testid="tags-em-alta-chips">
        {items.map((tag) => (
          <button
            key={tag.id}
            type="button"
            onClick={() => handleClick(tag)}
            className="group inline-flex items-center gap-1.5 bg-white hover:bg-[var(--primary)] text-[var(--primary)] hover:text-white border border-[var(--primary)]/20 hover:border-[var(--primary)] px-3 py-1.5 rounded-full text-sm font-medium transition-all shadow-sm hover:shadow-md active:scale-95"
            data-testid={`tags-em-alta-chip-${tag.id}`}
          >
            {tag.nome}
            <span className="text-xs opacity-60 group-hover:opacity-90">
              · {tag.contagem_municipio}
            </span>
            <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
          </button>
        ))}
      </div>
    </section>
  );
}
